import { Button } from "@netapp/bxp-design-system-react";
import { SearchIcon } from "@netapp/bxp-style/react-icons/Action";
import { CloseMonochromeIconsIcon as CloseIcon } from "@netapp/bxp-design-system-react/icons/monochrome";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import _ from "lodash";
import { globalSearch as i18nNamespace} from "../../../i18n/namespace";
import { SEARCH_MAX_LENGTH, SEARCH_MIN_LENGTH } from "../consoleObjects";
import { inputMethodFromInteraction } from "../globalSearchFullStory";
import { ObjectTypeFilterControl, defaultObjectTypeFilter, narrowedObjectTypes, normalizeObjectTypeFilter, suppressGlobalSearchClose, useObjectTypeFilter } from "./objectTypeFilter";
import styles from "../GlobalSearch.module.scss";

export const GLOBAL_SEARCH_FILTER_SLOT_ID = "global-search-object-filter";

const Input = React.forwardRef((props, inputRef) => {
    const { t } = useTranslation(i18nNamespace);
    const dispatch = useDispatch();
    const recentSearches = useSelector(state => state.globalSearch.recentSearches) || [];
    const isGlobalSearchOpen = useSelector(state => state.globalSearch.isOpen);
    const isSearchPerformed = useSelector(state => state.globalSearch.isSearchPerformed);
    const inputValue = useSelector(state => state.globalSearch.inputValue);
    const filterContext = useObjectTypeFilter();
    const [localFilter, setLocalFilter] = useState(defaultObjectTypeFilter);
    const objectTypeFilter = filterContext?.objectTypeFilter ?? localFilter;
    const objectTypeFilterRef = useRef(objectTypeFilter);
    objectTypeFilterRef.current = objectTypeFilter;
    const [text, setText] = useState("");
    const searchPayload = term => {
        const objectTypes = narrowedObjectTypes(objectTypeFilterRef.current);
        return {
            searchTerm: term,
            ...(objectTypes ? { objectTypes } : {})
        };
    };
    const applyObjectFilter = values => {
        suppressGlobalSearchClose();
        if (filterContext?.applyObjectFilter) {
            filterContext.applyObjectFilter(values);
            return;
        }
        setLocalFilter(normalizeObjectTypeFilter(values));
    };
    const showShortQuery = value => {
        dispatch({
            type: "GLOBAL-SEARCH/QUERY-TOO-SHORT",
            payload: value
        });
    };

    const debouncedTrigger = useMemo(() => {
        return _.debounce((value) => {
            dispatch({
                type: "GLOBAL-SEARCH/SEARCH",
                payload: searchPayload(value),
                track: value
            })
        }, 300);
    }, [dispatch])

    useEffect(() => {
        if(!isGlobalSearchOpen){
            debouncedTrigger.cancel();
        }
        return () => debouncedTrigger.cancel();
    }, [debouncedTrigger, isGlobalSearchOpen]);

    useEffect(() => {
        if (typeof inputValue === "string") {
            setText(inputValue);
        }
    }, [inputValue]);

    useEffect(() => {
        const input = inputRef?.current;
        if (!text || text !== inputValue || !input) {
            return;
        }
        input.focus();
        input.setSelectionRange(0, text.length);
    }, [text, inputValue, inputRef]);

    useEffect(() => {
        if (!isGlobalSearchOpen) {
            inputRef?.current?.blur();
        }
    }, [isGlobalSearchOpen, inputRef]);

    return <div className={styles.input}>
        <SearchIcon className={styles['search-icon']}/>
        <input ref={inputRef} value={text} maxLength={SEARCH_MAX_LENGTH} placeholder={t('bluexp_search')} aria-label={t('bluexp_search')} onChange={e => {
            const value = e.target.value;
            if (value.length > SEARCH_MAX_LENGTH) {
                return;
            }
            const term = value.trim();
            setText(value)
            if (!term) {
                debouncedTrigger.cancel();
                dispatch({ type: "GLOBAL-SEARCH/SET-INPUT", payload: "" });
                dispatch({ type: "GLOBAL-SEARCH/CLEAR" });
            } else if (term.length < SEARCH_MIN_LENGTH) {
                debouncedTrigger.cancel();
                showShortQuery(term);
            } else {
                debouncedTrigger(term);
            }
        }} onFocus={event => {
            dispatch({
                type: "GLOBAL-SEARCH/OPEN",
                inputMethod: inputMethodFromInteraction(event.nativeEvent)
            })
            const term = text.trim();
            if (term.length >= SEARCH_MIN_LENGTH && !isSearchPerformed) {
                dispatch({
                    type: "GLOBAL-SEARCH/SEARCH",
                    payload: searchPayload(term),
                    track: term
                })
            } else if (term.length > 0 && term.length < SEARCH_MIN_LENGTH) {
                showShortQuery(term);
            }
        }} onKeyDown={(e) => {
            if (e.key === "Enter") {
                const value = e.target.value.trim();
                if (!value) {
                    return;
                }
                debouncedTrigger.cancel();
                if (value.length < SEARCH_MIN_LENGTH) {
                    showShortQuery(value);
                    return;
                }
                dispatch({
                    type: "GLOBAL-SEARCH/SEARCH",
                    payload: searchPayload(value),
                    track: value
                });
                return;
            }
            if ((e.target.value === "" && _.isEmpty(recentSearches) && e.key === "Tab") || e.key === "Escape") {
                dispatch({
                    type: "GLOBAL-SEARCH/CLOSE"
                })
            }
        }}/>
        {(isGlobalSearchOpen || isSearchPerformed) && text?.length > 0 && <Button type="none" onClick={(e) => {
            debouncedTrigger.cancel();
            setText("");
            dispatch({
                type: "GLOBAL-SEARCH/SET-INPUT",
                payload: ""
            });
            dispatch({
                type: "GLOBAL-SEARCH/CLEAR"
            })
            inputRef.current.focus();
            /**
             * Need to prevent default cause once the text is cleared the button disappears but the propagation continues and it is considered as clicking outside the search widget
             */
            e.preventDefault();
            e.stopPropagation();
            return false;
        }} className={styles['clear-button']} ariaLabel={t('clear_search')}><CloseIcon className={styles['clear-icon']}/></Button>}
        {(isGlobalSearchOpen || isSearchPerformed) && <div
            id={GLOBAL_SEARCH_FILTER_SLOT_ID}
            className={styles['input-filter']}
            onMouseDown={event => event.preventDefault()}
        >
            <ObjectTypeFilterControl values={objectTypeFilter} onChange={applyObjectFilter} />
        </div>}
    </div>
})

export default Input;
